import { VissVehicleTileDataResponseWithId } from '../http/clients/viss/VissClientTeaser';
import { TPersonalizedVehicle } from '../schema/t4-personalization/PersonalizationData';
import { TranslationType } from '../util/i18n';
import {
  MixedDlsTeaserResponse,
  OptionalProductDto,
  OptionalProductDtoListWithId,
  ProductDto,
} from './dls/dls-response/ProductDto';
import {
  MixedDlsSearchAlternativeReponseWithId,
  SearchAlternativeReponseWithId,
  SearchResponseDTO,
} from './dls/dls-response/SearchResponseDto';
import { MixedOneSearchGraphQlResponse } from './one-search/onesearch-response/OneSearchGraphQlResponse';

import { InflateRequestMode } from '../../../types/dist/types/src/types/Modes';
import { TCampaignTileAemPayload } from '../schema/campaign-tile/CampaignTileData';
import { Query } from './one-search/queries';

export type ResponseTypes = ProductDto | ProductDto[] | SearchResponseDTO;

export type ResponseDataTypes = ProductDto;

export type T4ResponseType = {
  personalizedVehicle: TPersonalizedVehicle;
  responseTypeId: 'T4VehicleDto';
};

export type MixedT4Data = T4ResponseType & {
  market: string;
  lang: string;
  brand?: string;
  translations: (key: string, dataset?: TranslationType) => string;
};

export type CampaignTileResponseType = {
  rawData: TCampaignTileAemPayload;
  responseTypeId: 'CampaignTileVehicleDto';
};

export type MixedCampaignTileData = CampaignTileResponseType & {
  market: string;
  lang: string;
  brand?: string;
  translations: (key: string, dataset?: TranslationType) => string;
};

export type OptionalOneSearchGraphQlResponseWithRawData = Query & {
  responseTypeId: 'OneSearchGraphQlWithRawData';
  url?: string;
  rawData?: TCampaignTileAemPayload;
};

export type MixedOneSearchGraphQlWithRawDataResponse = Query & {
  responseTypeId: 'GraphQlVehicleWithRawData';
  brand?: string;
  ctx?: string;
  market: string;
  lang: string;
  dlsUrl?: string;
  mode: InflateRequestMode;
  rawData?: TCampaignTileAemPayload;
  translations: (key: string, dataset?: TranslationType) => string;
};

export type OptionalResponseTypes =
  | OptionalResponseDataTypes
  | OptionalProductDto
  | OptionalProductDtoListWithId
  | OptionalOneSearchGraphQlResponseWithRawData
  | VissVehicleTileDataResponseWithId;

export type OptionalResponseDataTypes =
  | OptionalProductDto
  | T4ResponseType
  | CampaignTileResponseType
  | SearchAlternativeReponseWithId;

export type MixedResponseType =
  | MixedOneSearchGraphQlResponse
  | MixedDlsTeaserResponse
  | MixedT4Data
  | MixedCampaignTileData
  | MixedDlsSearchAlternativeReponseWithId
  | VissVehicleTileDataResponseWithId;

export function isT4Personalized(response: OptionalResponseTypes): response is T4ResponseType {
  return response.responseTypeId === 'T4VehicleDto';
}

export function isTCampaignTile(response: OptionalResponseTypes): response is CampaignTileResponseType {
  return response.responseTypeId === 'CampaignTileVehicleDto';
}

export function isDlsTeaserList(response: OptionalResponseTypes): response is OptionalProductDtoListWithId {
  return response.responseTypeId === 'ProductDtoList';
}

export function isDlsTeaserResponse(response: OptionalResponseTypes): response is OptionalProductDto {
  return response.responseTypeId === 'ProductDto';
}

export function isDlsSearchAlternative(response: OptionalResponseTypes): response is SearchAlternativeReponseWithId {
  return response.responseTypeId === 'SearchResponseDTO';
}

export function isOneSearchGraphQlResponse(
  response: OptionalResponseTypes,
): response is OptionalOneSearchGraphQlResponseWithRawData {
  return response.responseTypeId === 'OneSearchGraphQlWithRawData';
}

export function isOneSearchWithRawDataResponse(
  response: OptionalResponseTypes,
): response is OptionalOneSearchGraphQlResponseWithRawData {
  return response.responseTypeId === 'OneSearchGraphQlWithRawData';
}

export function isVissVehicleTileDataQueryResponse(
  response: OptionalResponseTypes,
): response is VissVehicleTileDataResponseWithId {
  return response.responseTypeId === 'VissVehicleTileDataQuery';
}

export function isMixedOneSearchGraphQlResponse(
  response: MixedResponseType,
): response is MixedOneSearchGraphQlResponse {
  return response.responseTypeId === 'OneSearchGraphQlVehicle';
}

export function isMixedDlsTeaserResponse(response: MixedResponseType): response is MixedDlsTeaserResponse {
  return response.responseTypeId === 'ProductDto';
}

export function isMixedT4Data(response: MixedResponseType): response is MixedT4Data {
  return response.responseTypeId === 'T4VehicleDto';
}

export function isMixedCampaignTileData(response: MixedResponseType): response is MixedCampaignTileData {
  return response.responseTypeId === 'CampaignTileVehicleDto';
}

export function isMixedSearchAlternativeResponse(
  response: MixedResponseType,
): response is MixedDlsSearchAlternativeReponseWithId {
  return response.responseTypeId === 'SearchResponseDTO';
}
